export const formatCents = (amountCents: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amountCents / 100);

export const formatDate = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const formatCardBrand = (brand: string | null) => {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
};

export const formatMaskedLast4 = (last4: string | null) => (last4 ? `•••• ${last4}` : '••••');

export const formatCardExpiry = (expMonth: number | null, expYear: number | null) => {
  if (!expMonth || !expYear) return null;
  const month = String(expMonth).padStart(2, '0');
  const year = String(expYear % 100).padStart(2, '0');
  return `Exp ${month}/${year}`;
};

/**
 * [MCV-1858] Formats a calendar date (YYYY-MM-DD) without a timezone shift. `formatDate` parses the
 * string as UTC midnight, which renders as the previous day in the US.
 */
export const formatCalendarDate = (value: string | null | undefined) => {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return '—';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

/** 500 -> "5%", 250 -> "2.5%". */
export const formatRateBps = (rateBps: number) => `${Number((rateBps / 100).toFixed(2))}%`;

export const PROVIDER_TIER_LABEL = {
  PHYSICIAN: 'Physician (MD/DO)',
  APP: 'Advanced practice provider',
  NURSE: 'Nurse',
  ALLIED: 'Allied health',
} as const;

/** [MCV-1933] "Physician (MD/DO)" for a tier; null when the tier is absent or unknown. */
export const formatProviderTier = (tier: string | null | undefined) =>
  tier != null && tier in PROVIDER_TIER_LABEL
    ? PROVIDER_TIER_LABEL[tier as keyof typeof PROVIDER_TIER_LABEL]
    : null;

/** [MCV-1933] Whole-dollar amounts without cents ($199, $10), cents when they are not whole. */
export const formatDollars = (amountCents: number) =>
  amountCents % 100 === 0
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amountCents / 100)
    : formatCents(amountCents);
